package com.example.mystybag.url.logic

import com.example.mystybag.core.defines.UtilsActionsDefines.ACTION_NONE
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.ParentCallback
import com.example.mystybag.url.model.URLViewUIState
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay

interface URLViewPaymentCallback : ParentCallback {
    fun onPaymentSuccess(transactionReference: String, reservationId: Long)
    fun onPaymentFlowClosed(transactionReference: String, reservationId: Long)
}

class URLViewViewModel(
    context: AppContext,
    url: String,
    val text: String,
    postBody: String = "",
    successUrlPrefix: String = "",
    cancelUrlPrefix: String = "",
    transactionReference: String = "",
    reservationId: Long = 0L
): ChildViewModel<URLViewUIState>(
    context,
    URLViewUIState(
        url = url,
        title = text,
        postBody = postBody,
        successUrlPrefix = successUrlPrefix,
        cancelUrlPrefix = cancelUrlPrefix,
        transactionReference = transactionReference,
        reservationId = reservationId
    ),
    ACTION_NONE
) {
    private companion object {
        const val AUTO_RETURN_DELAY_MS = 10_000L
    }

    private var successHandled = false
    private var flowClosedHandled = false
    private var lastNavigatedUrl: String = ""
    private var autoReturnJob: Job? = null

    override fun onCreate() {
        navControls.title = text
        // Required so hardware/gesture back invokes onBackButtonClicked() instead of Decompose popping
        // the screen silently — otherwise BagViewModel never gets onPaymentFlowClosed / resetCheckoutFlowAfterReturn()
        // and checkoutInProgress stays true (reserve dialog stuck loading).
        overrideBackButton = true
    }

    override fun onDestroy() {
        // Safety net: swipe-back / stack changes can dismiss without onBackButtonClicked.
        if (!successHandled && !flowClosedHandled) {
            notifyPaymentFlowClosed()
        }
        super.onDestroy()
    }

    fun onNavigatedUrl(url: String) {
        if (url.isBlank()) return
        lastNavigatedUrl = url
        val successReason = successMatchReason(url)
        val cancelReason = cancelMatchReason(url)
        if (cancelReason != null) {
            closeFlowAndGoBack()
            return
        }
        if (successHandled) return
        if (successReason != null) {
            updateState { it.copy(showPaymentSuccessFooter = true) }
            scheduleAutoReturn()
        }
    }

    override fun onBackButtonClicked() {
        if (successHandled || isSuccessUrl(lastNavigatedUrl)) {
            autoReturnJob?.cancel()
            notifyPaymentSuccess()
            return
        }
        closeFlowAndGoBack()
    }

    fun onCheckoutSuccessSignal() {
        if (successHandled) return
        updateState { it.copy(showPaymentSuccessFooter = true) }
        scheduleAutoReturn()
    }

    fun onReservationsCtaClick() {
        notifyPaymentSuccess()
    }

    private fun isSuccessUrl(url: String): Boolean {
        return successMatchReason(url) != null
    }

    private fun successMatchReason(url: String): String? {
        val successPrefix = uiState.value.successUrlPrefix
        if (successPrefix.isNotBlank() && url.startsWith(successPrefix)) return "successPrefix"
        if (url.contains("/merchants/payment/success")) return "/merchants/payment/success"
        if (url.contains("status=success", ignoreCase = true)) return "status=success"
        if (url.contains("result=success", ignoreCase = true)) return "result=success"
        if (url.contains("payment-success", ignoreCase = true)) return "payment-success"
        if (url.contains("/checkout/success", ignoreCase = true)) return "/checkout/success"
        if (url.contains("/success", ignoreCase = true)) return "/success"
        // myPOS hosted success pages in sandbox/live before merchant callback
        if (url.contains("/paymentSuccess")) return "/paymentSuccess"
        if (url.contains("/userOk")) return "/userOk"
        return null
    }

    private fun isCancelUrl(url: String): Boolean {
        return cancelMatchReason(url) != null
    }

    private fun cancelMatchReason(url: String): String? {
        val cancelPrefix = uiState.value.cancelUrlPrefix
        if (cancelPrefix.isNotBlank() && url.startsWith(cancelPrefix)) return "cancelPrefix"
        // myPOS hosted failure/cancel pages before merchant callback
        if (url.contains("/paymentCancel")) return "/paymentCancel"
        if (url.contains("/paymentFailure")) return "/paymentFailure"
        if (url.contains("/userCancel")) return "/userCancel"
        return null
    }

    private fun notifyPaymentSuccess() {
        successHandled = true
        flowClosedHandled = true
        autoReturnJob?.cancel()
        autoReturnJob = null
        setResult<URLViewPaymentCallback> {
            onPaymentSuccess(
                transactionReference = uiState.value.transactionReference,
                reservationId = uiState.value.reservationId
            )
        }
    }

    private fun notifyPaymentFlowClosed() {
        if (flowClosedHandled) return
        flowClosedHandled = true
        autoReturnJob?.cancel()
        autoReturnJob = null
        setResult<URLViewPaymentCallback> {
            onPaymentFlowClosed(
                transactionReference = uiState.value.transactionReference,
                reservationId = uiState.value.reservationId
            )
        }
    }

    private fun closeFlowAndGoBack() {
        if (isSuccessUrl(lastNavigatedUrl)) {
            notifyPaymentSuccess()
            return
        }
        if (!flowClosedHandled) {
            notifyPaymentFlowClosed()
        }
        setResult<ParentCallback> { goBack() }
    }

    private fun scheduleAutoReturn() {
        if (autoReturnJob?.isActive == true) return
        autoReturnJob = launchOnMain {
            delay(AUTO_RETURN_DELAY_MS)
            if (!successHandled) {
                notifyPaymentSuccess()
            }
        }
    }
}